/* Iter249e · Objection Library
   ---------------------------------------------------------------
   Compact overlay that Marcelo pops open during a live cold-call
   or when composing the outreach email. 9 curated objections
   grouped by category with a 1-click "Copiar" per response so the
   rep can paste directly into their reply.
*/
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, MessageSquareText, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function ObjectionLibraryButton({ testid = "objection-library-btn" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
              data-testid={testid}
              title="Abrir biblioteca de objeções (9 respostas prontas)"
              className="h-9 px-3 rounded-lg font-bold text-[12.5px] text-white inline-flex items-center gap-1.5 disabled:opacity-50"
              style={{ background: "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)" }}>
        <MessageSquareText className="w-3.5 h-3.5" />
        Objeções
      </button>
      {open && <ObjectionLibraryModal onClose={() => setOpen(false)} />}
    </>
  );
}

function ObjectionLibraryModal({ onClose }) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/smith/outreach/objections`);
        setItems(data.items || []);
        setCategories(["All", ...(data.categories || [])]);
      } catch (e) {
        toast.error("Falha ao carregar objeções");
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = category === "All"
    ? items
    : items.filter((i) => i.category === category);

  const copy = async (it) => {
    try {
      await navigator.clipboard.writeText(it.response);
      setCopiedId(it.id);
      toast.success(`"${it.objection.slice(0, 40)}…" copiada`);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (_e) {
      toast.error("Copiar falhou — selecione o texto manualmente");
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
         data-testid="objection-library-modal"
         onClick={onClose}>
      <div className="w-full max-w-3xl my-8 rounded-2xl border border-amber-500/30 bg-slate-900 shadow-2xl"
           onClick={(e) => e.stopPropagation()}
           style={{ background: "linear-gradient(135deg,rgba(245,158,11,0.10) 0%,rgba(15,23,42,0.95) 60%)" }}>
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <MessageSquareText className="w-4 h-4 text-amber-400" />
            <h3 className="font-extrabold text-white text-[14px] uppercase tracking-wider">
              Objection Library · 9 respostas prontas
            </h3>
          </div>
          <button type="button" onClick={onClose}
                  data-testid="objection-library-close"
                  className="h-8 w-8 rounded-lg border border-white/15 text-white/70 hover:text-white hover:border-white/40 inline-flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-3">
          {/* Category filter chips */}
          <div className="flex flex-wrap gap-1.5" data-testid="objection-category-chips">
            {categories.map((c) => (
              <button key={c} type="button" onClick={() => setCategory(c)}
                      data-testid={`objection-cat-${c}`}
                      className={"text-[10.5px] px-2 py-1 rounded-full font-bold uppercase tracking-wider transition " +
                        (category === c
                          ? "bg-amber-500/40 text-amber-50 ring-1 ring-amber-400/70"
                          : "bg-white/5 text-slate-300 hover:bg-white/10")}>
                {c}
              </button>
            ))}
          </div>

          {/* Items */}
          {loading ? (
            <div className="text-[12px] text-slate-400 py-6 text-center"
                 data-testid="objection-loading">
              Carregando biblioteca…
            </div>
          ) : (
            <div className="space-y-2.5 max-h-[60vh] overflow-y-auto pr-1">
              {filtered.map((it) => {
                const isCopied = copiedId === it.id;
                return (
                  <div key={it.id}
                       data-testid={`objection-item-${it.id}`}
                       className="rounded-xl border border-white/10 bg-black/40 p-3 sm:p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1">
                        <span className="text-[9.5px] uppercase tracking-widest font-bold text-amber-300 mb-1 block">
                          {it.category}
                        </span>
                        <div className="text-white font-extrabold text-[13px] leading-snug"
                             data-testid={`objection-text-${it.id}`}>
                          "{it.objection}"
                        </div>
                      </div>
                      <button type="button" onClick={() => copy(it)}
                              data-testid={`objection-copy-${it.id}`}
                              className={"shrink-0 h-8 px-2.5 rounded-lg text-[11px] font-bold inline-flex items-center gap-1 transition " +
                                (isCopied
                                  ? "bg-emerald-500/30 text-emerald-200 ring-1 ring-emerald-400/60"
                                  : "bg-amber-500/25 text-amber-100 hover:bg-amber-500/40")}>
                        <Copy className="w-3 h-3" />
                        {isCopied ? "Copiado" : "Copiar"}
                      </button>
                    </div>
                    <div className="text-[12.5px] text-slate-200 leading-relaxed"
                         data-testid={`objection-response-${it.id}`}>
                      {it.response}
                    </div>
                    {it.use_when && (
                      <div className="text-[10.5px] text-slate-500 mt-2 italic">
                        <span className="text-slate-400 font-bold">Usar quando:</span> {it.use_when}
                      </div>
                    )}
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div className="text-[12px] text-slate-500 py-6 text-center">
                  Nenhuma objeção nesta categoria.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
